/**
 * Shared shapers for the Owners Hub Stats payloads — used by both `useHomeStats`
 * (Key Metrics) and `useHomeChannels` (Acquisition Channels). Keeps the bucket /
 * metrics parsing in one place so the two hooks read the API identically.
 *
 * Response shapes (verified live — see `docs/api.owners-hub.stats.md`):
 *  - history: `[{ date, data: [<group>] }]`, one bucket per `interval_slice`;
 *  - data-set: a single `<group>`;
 *  - a `<group>` is the flat metrics object (no dimensions) or `{ aggregate_on,
 *    data_points }` (dimensioned, e.g. the `aft`-grouped channel calls).
 */

export type Metrics = Record<string, unknown>;
export type Bucket = { date: string; metrics: Metrics };

export const toNumber = (value: unknown): number => {
  const n = typeof value === 'number' ? value : parseFloat(String(value ?? ''));
  return Number.isFinite(n) ? n : 0;
};

/** A bucket/group can be the flat metrics object or `{ data_points }`-wrapped. */
export const groupMetrics = (group: unknown): Metrics => {
  if (!group || typeof group !== 'object') return {};
  const obj = group as Metrics;
  const dp = obj.data_points;
  return dp && typeof dp === 'object' ? (dp as Metrics) : obj;
};

/** Metrics for a single history bucket (`{ date, data: [<group>] }`). Reads the
 *  first group — Home queries carry a single group (no dims, or one filtered value). */
export const bucketMetrics = (bucket: unknown): Metrics => {
  if (!bucket || typeof bucket !== 'object') return {};
  const data = (bucket as { data?: unknown }).data;
  return Array.isArray(data) ? groupMetrics(data[0]) : groupMetrics(bucket);
};

export const sumFields = (metrics: Metrics, fields: readonly string[]): number =>
  fields.reduce((sum, field) => sum + toNumber(metrics[field]), 0);

/** Per-bucket scaled series for a chart line (sum of `fields` per bucket, then `scale`). */
export const chartSeries = (buckets: Bucket[], fields: readonly string[], scale: number): number[] =>
  buckets.map((b) => sumFields(b.metrics, fields) * scale);

/** Total of `fields` summed across every bucket. */
export const sumBuckets = (buckets: Bucket[], fields: readonly string[]): number =>
  buckets.reduce((sum, b) => sum + sumFields(b.metrics, fields), 0);

/**
 * Prior-day vs current-day totals for the DayBars view (Today/Yesterday ranges).
 * Each is the FULL `sumBuckets` of `fields` over its period (× `scale`) — `previous`
 * is the complete prior day (the ghost target), `current` the selected day (so-far
 * on the live Today range). Mirrors the chart's `scale` so bars and figures agree.
 */
export const dayPair = (
  current: Bucket[],
  previous: Bucket[],
  fields: readonly string[],
  scale: number,
): { prev: number; current: number } => ({
  prev: sumBuckets(previous, fields) * scale,
  current: sumBuckets(current, fields) * scale,
});

/**
 * Peak of `fields` (summed per bucket) across every bucket. For metrics the backend
 * rolls up as a MAX rather than a sum (e.g. `active_players`, a peak count), summing
 * the per-bucket values would over-count — the window figure is the highest bucket.
 * Empty input is `0`.
 */
export const maxBuckets = (buckets: Bucket[], fields: readonly string[]): number =>
  buckets.reduce((max, b) => Math.max(max, sumFields(b.metrics, fields)), 0);

/**
 * Count-match the prior period to the elapsed part of the current one: the first
 * `current.length` prior buckets. Buckets are time-ordered, so `prior[i]` lines up
 * with `current[i]` — a partial live period (e.g. Today) compares against the same
 * elapsed slice of the prior period rather than a full one.
 */
export const matchedBaseline = (current: Bucket[], prior: Bucket[]): Bucket[] => prior.slice(0, current.length);

/** Split buckets at `currentStartMs` into `current` (≥ start) and `previous` (earlier),
 *  preserving time order. Used when one extended call carries both periods. */
export const splitByStart = (buckets: Bucket[], currentStartMs: number): { current: Bucket[]; previous: Bucket[] } => {
  const current: Bucket[] = [];
  const previous: Bucket[] = [];
  buckets.forEach((b) => {
    const t = Date.parse(b.date);
    (Number.isFinite(t) && t >= currentStartMs ? current : previous).push(b);
  });
  return { current, previous };
};

/**
 * Resolve the current + previous periods from however they were fetched:
 *  - `live` ranges (Today) make a SEPARATE prior call (so the current call stays in
 *    the backend's fresh window), so `main` IS the current period and `prior` is the
 *    separate baseline;
 *  - the rest make ONE extended call that carries both, split at `currentStartMs`.
 */
export const resolvePeriods = (
  main: Bucket[],
  prior: Bucket[],
  live: boolean,
  currentStartMs: number,
): { current: Bucket[]; previous: Bucket[] } =>
  live ? { current: main, previous: prior } : splitByStart(main, currentStartMs);

/** Drop buckets dated after `nowMs` (the live current range may report future, not
 *  yet aggregated, slices as zero — without this the trend would dip to zero). */
export const liveBuckets = (buckets: Bucket[], nowMs: number): Bucket[] =>
  buckets.filter((b) => {
    const t = Date.parse(b.date);
    return !Number.isFinite(t) || t <= nowMs;
  });

/** The raw history payload's bucket array (a bare array, tolerating a `{ data: [] }` envelope). */
const historyList = (raw: unknown): unknown[] =>
  Array.isArray(raw)
    ? raw
    : raw && typeof raw === 'object' && Array.isArray((raw as { data?: unknown }).data)
      ? (raw as { data: unknown[] }).data
      : [];

/** Normalise the raw history payload (a bare array, tolerating a `{ data: [] }` envelope). */
export const toBuckets = (raw: unknown): Bucket[] =>
  historyList(raw).map((item) => ({
    date: String((item as { date?: unknown })?.date ?? ''),
    metrics: bucketMetrics(item),
  }));

/** The value a group's `aggregate_on` carries for `dimension` (e.g. the `aft` of an
 *  Acquisition Channel group), or `undefined` when the group is flat / lacks it. The
 *  server echoes the grouping key per group, so we select on it client-side. */
export const groupDimension = (group: unknown, dimension: string): string | undefined => {
  if (!group || typeof group !== 'object') return undefined;
  const agg = (group as { aggregate_on?: unknown }).aggregate_on;
  if (!agg || typeof agg !== 'object') return undefined;
  const value = (agg as Record<string, unknown>)[dimension];
  return value === undefined || value === null ? undefined : String(value);
};

/**
 * Per-dimension-value buckets from a history payload grouped on `dimension`
 * (`aggregate_on: [dimension]`, with or without a matching `filter`). For each time
 * bucket it selects the group whose `aggregate_on[dimension]` equals `value` (a
 * channel's `aft`) and returns that group's metrics; a bucket with no matching group
 * yields EMPTY metrics, so the date axis is preserved and the figure reads zero (not
 * `–`). The selection is client-side off the echoed key, so it is robust whether the
 * server narrows to the one filtered group or echoes several. The backend rejects an
 * object-form `aggregate_on` (it must be a string array), so the value is sent via
 * `filter` and matched on the way back here. See `useHomeChannels`.
 */
export const toBucketsFor = (raw: unknown, dimension: string, value: string): Bucket[] =>
  historyList(raw).map((item) => {
    const date = String((item as { date?: unknown })?.date ?? '');
    const data = (item as { data?: unknown }).data;
    const groups = Array.isArray(data) ? data : [];
    const match = groups.find((g) => groupDimension(g, dimension) === value);
    return { date, metrics: match ? groupMetrics(match) : {} };
  });

/**
 * Per-date buckets that SUM the metrics of every group whose `dimension` value is in
 * `values` (e.g. the acquisition-channel `aft`s) into a single bucket per date.
 * Unlike {@link toBucketsFor} (one value), this aggregates ACROSS several dimension
 * values, so the one-bucket-per-date shape the period split / count-match rely on is
 * preserved. Groups outside `values` (the empty `aft`, plus non-marketing `aft`s like
 * `direct` / `organic`) are excluded, so the total counts only acquisition records.
 * A bucket with no matching group yields EMPTY metrics (reads zero, keeps the axis).
 */
export const toBucketsForAfts = (raw: unknown, dimension: string, values: readonly string[]): Bucket[] =>
  historyList(raw).map((item) => {
    const date = String((item as { date?: unknown })?.date ?? '');
    const data = (item as { data?: unknown }).data;
    const groups = Array.isArray(data) ? data : [];
    const metrics: Record<string, number> = {};
    groups.forEach((group) => {
      const dim = groupDimension(group, dimension);
      if (dim === undefined || !values.includes(dim)) return;
      const groupM = groupMetrics(group);
      Object.keys(groupM).forEach((key) => {
        metrics[key] = toNumber(metrics[key]) + toNumber(groupM[key]);
      });
    });
    return { date, metrics };
  });

/** Metrics object for a `data-set` payload (flat or `{ data_points }`-wrapped). */
export const dataSetMetrics = (raw: unknown): Metrics => groupMetrics(raw);

/**
 * Period-over-period delta (%), one decimal. Compares the CURRENT window total to
 * the baseline-window total over RAW field sums (display scaling cancels in the
 * ratio).
 *
 * From a ZERO baseline any non-zero value reads as a full **±100%** (a full
 * appearance / disappearance), so "1 vs 0" shows **+100%** — symmetric with
 * "0 vs 1" showing −100% — rather than +0%. "0 vs 0" stays 0% (no change). This
 * also avoids the ±Infinity a raw ratio would produce.
 */
export const deltaPct = (current: number, baseline: number): number => {
  if (baseline === 0) return current === 0 ? 0 : current > 0 ? 100 : -100;
  return Math.round(((current - baseline) / Math.abs(baseline)) * 1000) / 10;
};
